import { Location } from '@angular/common';
import { inject, Inject, Injectable, Injector, Optional } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  OKTA_AUTH,
  OktaAuthConfigService,
  OktaAuthGuard,
} from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { from, Observable, of } from 'rxjs';
import { ESQEP_AUTH_TYPE } from '../constants/constants.component';
import { AuthService } from '../services/auth/auth.service';
import { AUTHENTICATION_CONFIGURATION_NAMES } from '../services/auth/authenticationConfigNames';
import { ConfigService } from '../services/config/config.service';
import { BrowserStorageService } from '../utilities/browser-storage/browser-storage.service';
import { AuthGuard } from './auth.guard';

@Injectable({
  providedIn: 'root',
})
export class PreAuthGuard {
  private readonly browserStorage = inject(BrowserStorageService);

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private router: Router,

    @Optional()
    @Inject(MSAL_GUARD_CONFIG)
    private msalGuardConfig: MsalGuardConfiguration,
    @Optional()
    @Inject(MsalBroadcastService)
    private msalBroadcastService: MsalBroadcastService,
    @Optional() @Inject(MsalService) private msalAuthService: MsalService,
    @Optional() private location: Location,
    @Optional()
    @Inject(OktaAuthConfigService)
    private oktaConfig: OktaAuthConfigService,
    @Optional() @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    @Optional() private injector: Injector,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<any> {
    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.CSOD] &&
      route.queryParams.token &&
      !route.queryParams.error
    ) {
      if (
        this.browserStorage.get(ESQEP_AUTH_TYPE) !==
        AUTHENTICATION_CONFIGURATION_NAMES.CSOD
      ) {
        this.browserStorage.set(
          ESQEP_AUTH_TYPE,
          AUTHENTICATION_CONFIGURATION_NAMES.CSOD,
        );
      }
    }

    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.CSOD] &&
      this.browserStorage.get(ESQEP_AUTH_TYPE) ===
        AUTHENTICATION_CONFIGURATION_NAMES.CSOD
    ) {
      return new AuthGuard(this.authService, this.router).canActivate(
        route,
        state,
      );
    }

    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA] &&
      this.browserStorage.get(ESQEP_AUTH_TYPE) ===
        AUTHENTICATION_CONFIGURATION_NAMES.OKTA
    ) {
      return from(
        new OktaAuthGuard(
          this.oktaAuth,
          this.injector,
          this.oktaConfig,
        ).canActivate(route, state),
      );
    }

    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA] &&
      this.browserStorage.get(ESQEP_AUTH_TYPE) ===
        AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
    ) {
      return new MsalGuard(
        this.msalGuardConfig,
        this.msalBroadcastService,
        this.msalAuthService,
        this.location,
        this.router,
      ).canActivate(route, state);
    }

    // Tolerate refresh: if auth type is missing but we already have a valid session/token
    // let downstream guards/auth logic establish authentication instead of forcing /login.
    // CSOD SAML token is stored by AuthGuard under 'esqep.auth.saml'. If it exists, run AuthGuard to restore state.
    try {
      const hasSamlToken = !!localStorage.getItem('esqep.auth.saml');
      if (
        hasSamlToken &&
        this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.CSOD]
      ) {
        if (
          this.browserStorage.get(ESQEP_AUTH_TYPE) !==
          AUTHENTICATION_CONFIGURATION_NAMES.CSOD
        ) {
          this.browserStorage.set(
            ESQEP_AUTH_TYPE,
            AUTHENTICATION_CONFIGURATION_NAMES.CSOD,
          );
        }
        return new AuthGuard(this.authService, this.router).canActivate(
          route,
          state,
        );
      }
    } catch {}

    // If we truly cannot determine auth type or existing session, redirect to login.
    if (!this.browserStorage.get(ESQEP_AUTH_TYPE)) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return of(false);
    }

    return of(true);
  }
}
